import { useMemo, useState } from "react";
import { connect } from "react-redux";
import { Container, Typography, Box, Grid } from "@material-ui/core";
import { useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { TicketLog } from "../../interface/api/TicketLog";
import PageHero from "../shared/page-hero";
import ticketService from "../../services/ticketService";
import smallQuantityTicketService from "../../services/smallQuantityTicketService";
import hydrovacTicketService from "../../services/hydrovacTicketService";
import rubbleTicketService from "../../services/rubbleTicketService";
import { Ticket } from "../../interface/api/Ticket";
import { TicketPayload } from "../../interface/api/TicketPayload";
import { useAppSelector } from "../../hooks";
import { getCurrentUser } from "../../store/userStore";
import { history } from "../..";
import useWindowSize from "../../hooks/useWindowSize";
import Button from "../shared/Button";
import RecordDiffViewer from "../shared/record-diff-viewer";

type StreamType = "main" | "smallQuantity" | "hydrovac" | "rubble";

const getStreamFromPath = (pathname: string): StreamType => {
  if (pathname.startsWith("/small-quantity-ticket-log")) return "smallQuantity";
  if (pathname.startsWith("/hydrovac-ticket-log")) return "hydrovac";
  if (pathname.startsWith("/rubble-ticket-log")) return "rubble";
  return "main";
};

const streamConfig = {
  main: {
    fetchLogs: (id: string) => ticketService.getTicketLogs(id),
    fetchTicket: (id: string) => ticketService.getTicket(id),
    redirectPath: "/ticket",
  },
  smallQuantity: {
    fetchLogs: (id: string) => smallQuantityTicketService.getTicketLogs(id),
    fetchTicket: (id: string) => smallQuantityTicketService.getTicket(id),
    redirectPath: "/small-quantity-ticket",
  },
  hydrovac: {
    fetchLogs: (id: string) => hydrovacTicketService.getTicketLogs(id),
    fetchTicket: (id: string) => hydrovacTicketService.getTicket(id),
    redirectPath: "/hydrovac-ticket",
  },
  rubble: {
    fetchLogs: (id: string) => rubbleTicketService.getTicketLogs(id),
    fetchTicket: (id: string) => rubbleTicketService.getTicket(id),
    redirectPath: "/rubble-ticket",
  },
};

const streamTitles: Record<StreamType, string> = {
  main: "Ticket Log",
  smallQuantity: "Small Quantity Ticket Log",
  hydrovac: "Hydrovac Ticket Log",
  rubble: "Rubble Ticket Log",
};

const TicketLogView = ({}) => {
  const { id }: any = useParams();
  const location = useLocation();
  const stream = getStreamFromPath(location.pathname);
  const config = streamConfig[stream];
  const [ticketLogs, setTicketLogs] = useState<TicketLog[]>([]);
  const [ticketId, setTicketId] = useState<number | null>(null);
  const user = useAppSelector(getCurrentUser);
  const [restoring, setRestoring] = useState(false);
  const { isMobile } = useWindowSize();

  useMemo(() => {
    const init = async () => {
      let response = await config.fetchLogs(id);
      if (response.ok) {
        const data = response.data as TicketLog[];
        data.sort((a, b) => (a.date > b.date ? -1 : a.date < b.date ? 1 : 0));
        setTicketLogs(data);
      }

      response = await config.fetchTicket(id);
      if (response.ok) {
        const data = response.data as any;
        setTicketId(data.id);
      }
    };
    init();
  }, [id]);

  const onRestore = async (oldData: string) => {
    if (ticketId === null) return;

    setRestoring(true);

    const parsed = JSON.parse(oldData);

    if (stream === "main") {
      const _ticket = parsed as unknown as Ticket;
      const ticketPayload: TicketPayload = {
        ticket: _ticket,
        loggedInUserId: user ? user.id : undefined,
        createTransportIfDoesNotExist: false,
      };
      ticketService.saveTicket(ticketPayload).then((response) => {
        if (response.ok) {
          toast.success("Ticket restored");
          history.push(`${config.redirectPath}/${ticketId}`);
        } else {
          toast.error(response.originalError.message);
        }
        setRestoring(false);
      });
    } else if (stream === "smallQuantity") {
      smallQuantityTicketService.saveTicket(parsed).then((response) => {
        if (response.ok) {
          toast.success("Ticket restored");
          history.push(`${config.redirectPath}/${ticketId}`);
        } else {
          toast.error(response.originalError.message);
        }
        setRestoring(false);
      });
    } else if (stream === "hydrovac") {
      hydrovacTicketService.saveTicket(parsed).then((response) => {
        if (response.ok) {
          toast.success("Ticket restored");
          history.push(`${config.redirectPath}/${ticketId}`);
        } else {
          toast.error(response.originalError.message);
        }
        setRestoring(false);
      });
    } else {
      rubbleTicketService.saveTicket(parsed).then((response) => {
        if (response.ok) {
          toast.success("Ticket restored");
          history.push(`${config.redirectPath}/${ticketId}`);
        } else {
          toast.error(response.originalError.message);
        }
        setRestoring(false);
      });
    }
  };

  const ticketChanges = (ticketLog: TicketLog, index: number) => {
    return (
      <div
        key={ticketLog.id}
        style={{ borderTop: index === 0 ? "" : "2px solid", padding: "15px" }}
      >
        <div>
          <Grid container>
            <Grid item xs={12} md={6}>
              <Box sx={{ marginBottom: 15 }}>
                <Typography>
                  <b>Change Id:</b> {ticketLog.id}
                </Typography>
                <Typography>
                  <b>Timestamp:</b> {new Date(ticketLog.date).toLocaleString()}
                </Typography>
                <Typography>
                  <b>Username:</b> {ticketLog.username}
                </Typography>
                <Typography>
                  <b>First Name:</b> {ticketLog.firstName}
                </Typography>
                <Typography>
                  <b>Last Name:</b> {ticketLog.lastName}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
                <Button
                  label={restoring ? "Restoring" : `Restore ID:${ticketLog.id}`}
                  type="button"
                  variant="contained"
                  color="secondary"
                  disabled={restoring}
                  onClick={() => onRestore(ticketLog.currentDataObject)}
                />
              </Grid>
            </Grid>
          </Grid>
        </div>
        <RecordDiffViewer
          oldValue={ticketLog.currentDataObject}
          newValue={ticketLog.updatedDataObject}
          splitView={!isMobile}
          oldTitle="Before"
          newTitle="After"
        />
      </div>
    );
  };

  return (
    <Container maxWidth="xl">
      <PageHero
        title={streamTitles[stream]}
        subtitle="A Log of Ticket Edits."
        showBackButton={true}
      />
      {ticketLogs.length ? (
        ticketLogs.map((log, index) => ticketChanges(log, index))
      ) : (
        <PageHero
          title="No ticket logs found"
          subtitle=""
          showBackButton={false}
        />
      )}
    </Container>
  );
};

export default connect()(TicketLogView);
